import React, { useState, useEffect, useRef } from 'react'
import { useAuthStore } from '../store/useAuthStore'
import { supabase } from '../lib/supabaseClient'

const ProjectChat = ({ projectId }) => {
  const { user, profile } = useAuthStore()
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [chatError, setChatError] = useState(null)
  const [debugInfo, setDebugInfo] = useState({})
  const [hasAccess, setHasAccess] = useState(false)
  const [accessLoading, setAccessLoading] = useState(true)
  const messagesEndRef = useRef(null)
  const [projectMembers, setProjectMembers] = useState([])
  const [projectClient, setProjectClient] = useState(null)
  const [userProfilesMap, setUserProfilesMap] = useState({})
  const [participantCounts, setParticipantCounts] = useState({ employees: 0, clients: 0, admins: 0 })

  const checkChatAccess = async () => {
    try {
      console.log('🔍 Checking chat access for user:', profile?.id)
      if (profile?.role === 'admin') {
        console.log('✅ Admin access granted')
        setHasAccess(true)
        setAccessLoading(false)
        return
      }
      const { data: memberData } = await supabase
        .from('project_members')
        .select('user_id')
        .eq('project_id', projectId)
        .eq('user_id', profile?.id)
      if (memberData && memberData.length > 0) {
        console.log('✅ Employee access granted')
        setHasAccess(true)
        setAccessLoading(false)
        return
      }
      const { data: projectData } = await supabase
        .from('projects')
        .select('client_id')
        .eq('id', projectId)
        .eq('client_id', profile?.id)
        .single()
      if (projectData) {
        console.log('✅ Client access granted')
        setHasAccess(true)
        setAccessLoading(false)
        return
      }
      console.log('❌ No access to project chat')
      setHasAccess(false)
      setAccessLoading(false)
    } catch (error) {
      console.error('❌ Error checking chat access:', error)
      setHasAccess(false)
      setAccessLoading(false)
    }
  }

  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }
  useEffect(() => { scrollToBottom() }, [messages])
  useEffect(() => { if (projectId && profile) checkChatAccess() }, [projectId, profile])

  const getDisplayProfile = (userId) => {
    if (!userId) return null
    if (userProfilesMap[userId]) return userProfilesMap[userId]
    if (projectClient && projectClient.id === userId) return projectClient
    const member = projectMembers.find(m => m.user_id === userId && m.profile)
    if (member) return member.profile
    if (profile && profile.id === userId) return profile
    return null
  }

  const fetchProfileById = async (id) => {
    try {
      if (!id) return null
      const cached = getDisplayProfile(id)
      if (cached) return cached
      const { data, error } = await supabase
        .from('profiles')
        .select('id,name,email')
        .eq('id', id)
        .single()
      if (error) return null
      setUserProfilesMap(prev => ({ ...prev, [id]: data }))
      return data
    } catch {
      return null
    }
  }

  useEffect(() => {
    const memberIds = new Set(projectMembers.map(m => m.user_id))
    const participants = new Set([...memberIds])
    if (projectClient?.id) participants.add(projectClient.id)
    if (profile?.role === 'admin') participants.add(profile.id)
    let employees = 0, clients = 0, admins = 0
    participants.forEach((id) => {
      const p = getDisplayProfile(id)
      if (p?.role === 'admin') admins += 1
      else if (p?.role === 'client' || id === projectClient?.id) clients += 1
      else if (p?.role === 'employee' || memberIds.has(id)) employees += 1
    })
    setParticipantCounts({ employees, clients, admins })
  }, [projectMembers, projectClient, profile, userProfilesMap])

  useEffect(() => {
    if (!projectId || !hasAccess) return
    setLoading(true)

    const fetchMembers = async () => {
      const { data } = await supabase
        .from('project_members')
        .select('id,user_id')
        .eq('project_id', projectId)
      if (data) {
        const combined = []
        for (const m of data) {
          const p = await fetchProfileById(m.user_id)
          combined.push({ ...m, profile: p })
        }
        setProjectMembers(combined)
      }
    }

    const fetchClient = async () => {
      const { data: projectData } = await supabase
        .from('projects')
        .select('client_id')
        .eq('id', projectId)
        .single()
      if (projectData?.client_id) {
        const p = await fetchProfileById(projectData.client_id)
        setProjectClient(p)
      }
    }

    const fetchMessages = async () => {
      try {
        setDebugInfo(prev => ({ ...prev, fetching: true, error: null }))
        let { data, error } = await supabase
          .from('project_messages')
          .select('*')
          .eq('project_id', projectId)
        if (error) {
          console.error('❌ Error fetching messages (all cols):', error)
          setChatError(error.message || 'Failed to load chat messages')
          setDebugInfo(prev => ({ ...prev, error: error.message, count: 0 }))
          setMessages([])
          return
        }
        const withProfiles = []
        for (const m of data || []) {
          const p = getDisplayProfile(m.user_id) || await fetchProfileById(m.user_id)
          withProfiles.push({ ...m, profile: p })
        }
        withProfiles.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
        setMessages(withProfiles)
        setDebugInfo(prev => ({ ...prev, count: withProfiles.length, error: null }))
        setChatError(null)
      } catch (e) {
        console.error('❌ Exception fetching messages:', e)
        setDebugInfo(prev => ({ ...prev, error: e.message, count: 0 }))
        setChatError(e.message || 'Failed to load chat messages')
        setMessages([])
      } finally {
        setLoading(false)
        setDebugInfo(prev => ({ ...prev, fetching: false }))
      }
    }

    fetchMembers()
    fetchClient()
    fetchMessages()

    const channel = supabase
      .channel(`project-${projectId}-chat`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'project_messages', filter: `project_id=eq.${projectId}` },
        async (payload) => {
          const { data, error } = await supabase
            .from('project_messages')
            .select('*')
            .eq('id', payload.new.id)
            .single()
          if (error) {
            console.error('❌ Realtime fetch error:', error)
            setChatError(error.message)
            return
          }
          const p = getDisplayProfile(data.user_id) || await fetchProfileById(data.user_id)
          setMessages(prev => [...prev, { ...data, profile: p }])
          setChatError(null)
        }
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [projectId, hasAccess])

  const sendMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || sending) return
    setSending(true)

    const payloadFull = {
      project_id: projectId,
      user_id: user.id,
      content: newMessage.trim(),
      sender_name: profile?.name || null,
      sender_email: profile?.email || null,
      sender_avatar_url: profile?.avatar_url || null
    }

    const payloadMinimal = {
      project_id: projectId,
      user_id: user.id,
      content: newMessage.trim()
    }

    // Try full payload first
    let { error } = await supabase.from('project_messages').insert(payloadFull)

    // If schema cache missing new columns, retry minimal payload
    if (error && (error.code === 'PGRST204' || /schema cache/i.test(error.message || ''))) {
      const retry = await supabase.from('project_messages').insert(payloadMinimal)
      error = retry.error
    }

    if (!error) {
      setNewMessage('')
      setChatError(null)
    } else {
      console.error('Error sending message:', error)
      setChatError(error.message)
    }
    setSending(false)
  }

  const formatTime = (timestamp) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now - date
    if (diff < 60000) return 'Just now'
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
    return date.toLocaleDateString()
  }

  if (accessLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <div className="ml-2 text-xs text-gray-500">Checking access...</div>
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Restricted</h3>
          <p className="mt-1 text-sm text-gray-500">Only assigned employees and project clients can access this chat.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <div className="ml-2 text-xs text-gray-500">Loading messages...</div>
      </div>
    )
  }

  const getSenderInitial = (m) =>
    m.profile?.name?.charAt(0) || m.sender_name?.charAt(0) || m.profile?.email?.charAt(0) || m.sender_email?.charAt(0) || 'U'
  const getSenderName = (m) =>
    m.profile?.name || m.sender_name || m.profile?.email || m.sender_email || 'Unknown'
  const getSenderAvatar = (m) => m.profile?.avatar_url || m.sender_avatar_url || null

  return (
    <div className="flex flex-col h-full min-h-[85vh] bg-gradient-to-b from-gray-50 to-white">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm px-5 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Project Chat</h3>
            <p className="text-xs text-gray-500">Participants: {projectMembers.length + (projectClient ? 1 : 0) + (profile?.role === 'admin' ? 1 : 0)}</p>
          </div>
          <div className="flex items-center space-x-2 text-[11px]">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Employees: {participantCounts.employees}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-800">Clients: {participantCounts.clients}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">Admins: {participantCounts.admins}</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4 bg-gradient-to-b from-gray-50 to-white">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="mt-2">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className={`flex ${message.user_id === user.id ? 'justify-end' : 'justify-start'}`}>
              <div className={`flex max-w-[75%] ${message.user_id === user.id ? 'flex-row-reverse' : 'flex-row'}`}>
                <div className={`flex-shrink-0 ${message.user_id === user.id ? 'ml-3' : 'mr-3'}`}>
                  <div className={`h-9 w-9 rounded-full ring-2 ${message.user_id === user.id ? 'ring-blue-200 bg-blue-600' : 'ring-gray-200 bg-gray-400'} flex items-center justify-center overflow-hidden` }>
                    {getSenderAvatar(message) ? (
                      <img src={getSenderAvatar(message)} alt={getSenderName(message)} className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-sm font-semibold text-white">{getSenderInitial(message)}</span>
                    )}
                  </div>
                </div>
                <div className={`${message.user_id === user.id ? 'items-end' : 'items-start'} flex flex-col`}>
                  <div className={`px-4 py-2 rounded-2xl shadow-sm ${message.user_id === user.id ? 'bg-blue-600 text-white rounded-br-none' : 'bg-white text-gray-900 border border-gray-200 rounded-bl-none'}` }>
                    <p className="text-sm leading-relaxed">{message.content}</p>
                  </div>
                  <div className={`mt-1 text-[11px] text-gray-500 ${message.user_id === user.id ? 'text-right' : 'text-left'}`}>
                    <span className="font-medium text-gray-600">{getSenderName(message)}</span>
                    <span className="mx-1">•</span>
                    <span>{formatTime(message.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="bg-white/90 backdrop-blur border-t border-gray-200 px-4 py-3">
        <form onSubmit={sendMessage} className="flex items-center space-x-3">
          <div className="flex-1">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="w-full rounded-full bg-white border border-gray-200 px-4 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={sending}
            />
          </div>
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className={`inline-flex items-center justify-center rounded-full px-4 py-2 text-white shadow ${(!newMessage.trim() || sending) ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {sending ? (
              <div className="loading-spinner"></div>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

export default ProjectChat
